import { useEffect, useMemo, useRef, useState } from 'react';
import type { ActivityItem, Design, PowerState, Screen } from '@noorview/core';
import { SALAH_KEY_LABEL, appealTotals, isBroadcastLive, money, presetOf, dayTimes, formatDate, formatHijri, hhmm, hijriFor, indexTimetable, nextSalahBlank, salahBlankAt, ymd } from '@noorview/core';
import { attempt, backend, screenStatus, toast, seenAgo, STATUS_LABEL, STATUS_PILL, useApp, useScreenBroadcasts } from '../data/store';
import { Icon, type IconName } from '../ui/icons';
import { IconButton, Popover, confirm } from '../ui/controls';
import { StageView } from '../ui/StageView';
import { openSheet, useFocusScreen, useNav } from '../app/ui';
import { liveDesign } from '../app/designs';
import { setSystemLock, unlockedOr } from '../app/lock';

export function Dashboard() {
  const ws = useApp((s) => s.ws)!;
  const session = useApp((s) => s.session);
  const now = useApp((s) => s.now);
  const focusId = useFocusScreen((s) => s.id);
  const setFocus = useFocusScreen((s) => s.set);
  const bell = useRef<HTMLButtonElement>(null);
  const [notes, setNotes] = useState(false);
  const tz = ws.masjid.timezone;
  const date = new Date(now);
  const first = (session?.name ?? '').split(/\s+/)[0] || 'there';
  const unread = ws.activity.filter((a) => !a.read).length;
  // A notice or warning from Noor MS turns the bell red (white number) until it's read.
  const fromNoorMS = ws.activity.some((a) => !a.read && isNoorMS(a.kind));
  const focus = ws.screens.find((s) => s.id === focusId) ?? ws.screens[0];

  return (
    <div className="dash">
      <header className="dash-head">
        <div>
          <h1>As-salamu alaykum, {first}</h1>
          <p>{formatDate(date, tz, 'full')}  ·  {formatHijri(hijriFor(ymd(date, tz), ws.masjid.prayer.hijriAdjust))}</p>
        </div>
        <div className="spacer" />
        <button type="button" className="search-pill glass" onClick={() => openSheet({ name: 'search' })}>
          <Icon name="search" size={20} /><span>Search</span><span className="spacer" /><kbd>⌘K</kbd>
        </button>
        <IconButton ref={bell} icon="bell" label={fromNoorMS ? 'Notifications, including a message from Noor MS' : 'Notifications'} size="lg" iconSize={24} badge={unread} className={fromNoorMS ? 'noorms' : undefined} onClick={() => setNotes((v) => !v)} aria-expanded={notes} />
        <Popover anchor={bell.current} open={notes} onClose={() => setNotes(false)} width={400} label="Notifications">
          <Notifications items={ws.activity} onClose={() => setNotes(false)} />
        </Popover>
      </header>

      <RemoteLive now={now} />
      <div className="dash-grid">
        <div className="dash-left">
          <AllScreens screens={ws.screens} now={now} />
          <QuickActions />
        </div>
        <div className="dash-right">
          {focus ? <LivePreview screen={focus} now={now} /> : (
            <section className="card glass empty" style={{ minHeight: 420 }}>
              <b>No screens yet</b><span>Add your first screen and pair a display to get started.</span>
              <button type="button" className="btn primary" onClick={() => openSheet({ name: 'addScreen' })}>Add Screen</button>
            </section>
          )}
          <ScreensCard screens={ws.screens} now={now} focusId={focus?.id} onPick={setFocus} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Remote access is live ---------- */

/** While Noor MS has remote access: when it ends, and End access (owners and admins) or Manage. Not shown to Noor MS itself. */
function RemoteLive({ now }: { now: number }) {
  const session = useApp((s) => s.session);
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  const [until, setUntil] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pro = ws.masjid.plan === 'pro' && !session?.helping;
  useEffect(() => {
    if (!pro) return;
    let gone = false;
    const load = () => backend.support.help().then((h) => { if (!gone) setUntil(h); }).catch(() => {});
    void load();
    const t = setInterval(load, 60_000);
    return () => { gone = true; clearInterval(t); };
  }, [pro]);
  if (!pro || !until || Date.parse(until) <= now) return null;
  const can = session?.role === 'owner' || session?.role === 'admin';
  const end = async () => {
    setBusy(true);
    const ok = await attempt(() => backend.support.setHelp(0), 'Remote access ended. Noor MS can’t open your NoorView now.');
    setBusy(false);
    if (ok !== undefined) setUntil(null);
  };
  return (
    <section className="card glass remote-live" role="status" aria-label="Remote access is live">
      <span className="remote-live-ic"><Icon name="monitor" size={22} /></span>
      <div className="remote-live-text">
        <b>Remote access is live</b>
        <span>Noor MS has access until {hhmm(new Date(until), ws.masjid.timezone)}</span>
      </div>
      <div className="remote-live-actions">
        <button type="button" className="btn sm plain" onClick={() => nav('/settings/support?view=remote')}>Manage</button>
        {can && <button type="button" className="btn sm danger" disabled={busy} onClick={end}>End access</button>}
      </div>
    </section>
  );
}

/* ---------- All Screens ---------- */

function AllScreens({ screens, now }: { screens: Screen[]; now: number }) {
  // Counts follow what each paired screen has been told to do, so All On / All Off / Idle show straight away. A screen
  // with no display paired yet isn't on anything: it counts as a draft until a display is paired.
  const paired = screens.filter((s) => s.deviceId);
  const on = paired.filter((s) => s.power === 'on').length;
  const off = paired.filter((s) => s.power === 'off').length;
  const idle = paired.filter((s) => s.power === 'idle').length;
  const draft = screens.length - paired.length;
  const connected = screens.filter((s) => { const x = screenStatus(s, now); return x === 'online' || x === 'idle' || x === 'off'; }).length;
  const allPower = (p: PowerState) => paired.length > 0 && paired.every((s) => s.power === p);
  const allLocked = screens.length > 0 && screens.every((s) => s.locked);
  // Locked screens are left exactly as they are: All On / All Off / Idle only change the unlocked ones.
  const set = (patch: Partial<Pick<Screen, 'power' | 'locked'>>, msg: string) => {
    const open = screens.filter((s) => !s.locked);
    const kept = screens.length - open.length;
    const todo = open.filter((s) => s.power !== patch.power).map((s) => s.id);
    const note = kept ? `${msg}. ${kept === 1 ? `${screens.find((s) => s.locked)!.name} is locked, so it was left as it is` : `${kept} locked screens were left as they are`}.` : msg;
    if (!todo.length) { toast(kept ? note : msg); return Promise.resolve(true); }
    return attempt(() => backend.updateScreens(todo, patch), note);
  };
  // Lit in its own colour: All On green, All Off red, Idle orange, Lock blue.
  const controls: Array<{ icon: IconName; label: string; on: boolean; tone: 'on' | 'off' | 'idle' | 'lock'; run: () => void }> = [
    { icon: 'power', label: 'All On', tone: 'on', on: allPower('on') && !allLocked, run: () => void unlockedOr(() => void set({ power: 'on' }, 'All screens on')) },
    { icon: 'x', label: 'All Off', tone: 'off', on: allPower('off'), run: () => void unlockedOr(() => void set({ power: 'off' }, 'All screens off')) },
    { icon: 'pause', label: 'Idle', tone: 'idle', on: allPower('idle'), run: () => void unlockedOr(() => void set({ power: 'idle' }, 'All screens idle')) },
    { icon: allLocked ? 'lock' : 'unlock', label: allLocked ? 'Locked' : 'Lock', tone: 'lock', on: allLocked, run: () => void setSystemLock(!allLocked) },
  ];
  return (
    <section className="card open-sec" aria-labelledby="all-screens">
      <div className="card-head"><h2 id="all-screens">All Screens</h2><span className="spacer" /><span className="count" title="Displays connected right now">{connected} of {screens.length} connected</span></div>
      <div className="stats">
        <Stat n={on} label="On" color="var(--success)" />
        <Stat n={off} label="Off" color="var(--danger)" />
        <Stat n={idle} label="Idle" color="var(--warning)" />
        <Stat n={draft} label="Draft" color="var(--label3)" title="Screens with no display paired yet" />
      </div>
      <div className="controls">
        {controls.map((c) => (
          <button key={c.label} type="button" className={`control t-${c.tone}${c.on ? ' on' : ''}`} onClick={c.run} aria-pressed={c.on} disabled={!screens.length}>
            <span className="ring"><Icon name={c.icon} size={28} /></span><span>{c.label}</span>
          </button>
        ))}
      </div>
      <SalahStrip now={now} />
      <LiveStrip now={now} />
    </section>
  );
}

/** Salah mode at a glance, with a link to its settings (switching it on and off lives there). */
function SalahStrip({ now }: { now: number }) {
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  const m = ws.masjid, sm = m.prayer.salahMode;
  const info = useMemo(() => {
    if (!sm.enabled) return { active: false, text: 'Off · screens stay on during salah' };
    const tt = indexTimetable(ws.timetable);
    const getDay = (d: string) => dayTimes(m, tt, d);
    const at = new Date(now);
    const cur = salahBlankAt(m, getDay, at);
    if (cur) return { active: true, text: `Blank now for ${SALAH_KEY_LABEL[cur.key]} · back at ${hhmm(cur.until, m.timezone)}` };
    const nx = nextSalahBlank(m, getDay, at);
    return { active: false, text: nx ? `Next blank: ${SALAH_KEY_LABEL[nx.key]} at ${hhmm(nx.from, m.timezone)}` : 'On' };
  }, [m, ws.timetable, sm, Math.floor(now / 30_000)]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <button type="button" className={`salah-strip ctl${info.active ? ' active' : ''}`} onClick={() => nav('/settings/salah')}>
      <span className="ic"><Icon name="moon" size={18} /></span>
      <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}><b>Blank screens during salah</b><small>{info.text}</small></span>
      <span className="salah-edit">Edit<Icon name="chevron" size={16} /></span>
    </button>
  );
}

/** Messages and appeals on the screens right now, each with a way to manage or end it. */
function LiveStrip({ now }: { now: number }) {
  const list = useApp((s) => s.ws!.broadcasts).filter((b) => isBroadcastLive(b, new Date(now)));
  if (!list.length) return null;
  return (
    <div className="live-strip">
      {list.map((b) => {
        if (b.kind === 'khutbah') return (
          <div key={b.id} className="salah-strip ctl bc-live">
            <span className="ic" style={{ background: 'var(--success)', color: '#fff' }}><Icon name="khutbah" size={18} /></span>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}><b>Live khutbah</b>
              <small>{`On screens now${b.endsAt ? ` until ${hhmm(new Date(b.endsAt), useApp.getState().ws!.masjid.timezone)}` : ''}`}</small></span>
            <button type="button" className="btn sm secondary" onClick={() => openSheet({ name: 'khutbah' })}>Open</button>
            <button type="button" className="btn sm plain" onClick={() => void attempt(() => backend.stopKhutbah(), 'Live khutbah stopped')}>Stop</button>
          </div>
        );
        const appeal = b.kind === 'appeal' && b.appeal;
        return (
          <div key={b.id} className="salah-strip ctl bc-live">
            <span className="ic" style={{ background: appeal ? undefined : presetOf(b.message?.preset).color, color: appeal ? undefined : '#fff' }}><Icon name={appeal ? 'coins' : 'siren'} size={18} /></span>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}><b>{appeal ? `${b.appeal!.title}` : b.message?.heading}</b>
              <small>{appeal ? `${money(appealTotals(b.appeal!).raised, b.appeal!.currency)} raised · on screens now` : `On screens now${b.endsAt ? ` until ${hhmm(new Date(b.endsAt), useApp.getState().ws!.masjid.timezone)}` : ''}`}</small></span>
            {appeal && <button type="button" className="btn sm primary" onClick={() => openSheet({ name: 'appeal' })}>Add gift</button>}
            <button type="button" className="btn sm secondary" onClick={() => openSheet({ name: 'editBroadcast', id: b.id })}>Edit</button>
            <button type="button" className="btn sm plain" onClick={() => void attempt(() => backend.endBroadcast(b.id), appeal ? 'Appeal ended' : 'Message taken down')}>End</button>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ n, label, color, title }: { n: number; label: string; color: string; title?: string }) {
  return <div className="stat ctl" title={title}><b>{n}</b><span><i className="dot" style={{ background: color }} />{label}</span></div>;
}

/* ---------- Quick Actions ---------- */

function QuickActions() {
  const nav = useNav();
  const actions: Array<{ icon: IconName; title: string; sub: string; run: () => void }> = [
    { icon: 'siren', title: 'Urgent Message', sub: 'Janazah, car, notice', run: () => openSheet({ name: 'message' }) },
    { icon: 'coins', title: 'Live Appeal', sub: 'Gifts as they come in', run: () => openSheet({ name: 'appeal' }) },
    { icon: 'khutbah', title: 'Live Khutbah', sub: 'Translated live', run: () => openSheet({ name: 'khutbah' }) },
    { icon: 'schedule', title: 'Schedule', sub: 'A template at a set time', run: () => openSheet({ name: 'schedule' }) },
    { icon: 'upload', title: 'Upload CSV', sub: 'Prayer timetable', run: () => openSheet({ name: 'csv' }) },
    { icon: 'calendar', title: 'Jumu‘ah Times', sub: 'Khutbah & jama‘ah', run: () => openSheet({ name: 'jumuah' }) },
    { icon: 'moon', title: 'Eid Times', sub: 'Salah & takbeer', run: () => openSheet({ name: 'eid' }) },
    { icon: 'pencil', title: 'Edit Timetable', sub: 'Full year view', run: () => nav('/timetable') },
    { icon: 'chart', title: 'Analytics', sub: 'How your screens are used', run: () => openSheet({ name: 'analytics' }) },
    { icon: 'plus', title: 'Add Screen', sub: 'Pair a new display', run: () => openSheet({ name: 'addScreen' }) },
  ];
  return (
    <section className="card open-sec quick" aria-labelledby="qa">
      <div className="card-head"><h2 id="qa">Quick Actions</h2></div>
      <div className="qa-grid">
        {actions.map((a) => (
          <button key={a.title} type="button" className="qa" onClick={a.run}>
            <span className="ring"><Icon name={a.icon} size={26} /></span>
            <b>{a.title}</b>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ---------- Live preview ---------- */

function LivePreview({ screen, now }: { screen: Screen; now: number }) {
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  const bcs = useScreenBroadcasts(screen.id);
  const design = liveDesign(ws, screen.id, now);
  const st = screenStatus(screen, now);
  const live = st === 'online' || st === 'idle' || st === 'off';
  return (
    <section className="card glass live" aria-labelledby="live-title">
      <div className="card-head">
        <h2 id="live-title" className="big-title">{screen.name}</h2>
        <span className={`pill lg ${live ? 'online' : STATUS_PILL[st]}`}>{live ? 'Live' : STATUS_LABEL[st]}</span>
        {screen.locked && <span className="pill lg off" title="Locked: nothing on this screen can be changed until it’s unlocked">Locked</span>}
        <span className="spacer" />
        {/* Its own group, so the buttons move under the name together when the card is narrow, never out of it */}
        <div className="live-actions">
        {/* Pair display until a display is paired, then Unpair display in its place (without going to Screens). */}
        {!screen.deviceId
          ? <button type="button" className="btn secondary" disabled={screen.locked} onClick={() => openSheet({ name: 'pair', screenId: screen.id })} title={screen.locked ? 'Unlock this screen to pair a display' : 'Connect a TV or screen'}><Icon name="tv" size={18} />Pair display</button>
          : <button type="button" className="btn secondary" disabled={screen.locked} title={screen.locked ? 'Unlock this screen to unpair its display' : 'Release this screen’s display'}
              onClick={async () => { if (await confirm(`Unpair ${screen.name}?`, { body: 'The display will go back to showing a pairing code.', ok: 'Unpair' })) await attempt(() => backend.unpairScreen(screen.id), 'Display unpaired'); }}><Icon name="link" size={18} />Unpair display</button>}
        <IconButton icon="expand" label="Full screen" onClick={() => openSheet({ name: 'fullscreen', screenId: screen.id })} />
        <button type="button" className="btn primary" onClick={() => nav(`/studio/${screen.id}?start=live`)}>Edit in Studio</button>
        </div>
      </div>
      {design ? (
        <button type="button" className="live-frame" onClick={() => openSheet({ name: 'fullscreen', screenId: screen.id })} aria-label={`Open ${screen.name} full screen`}>
          <StageView design={design} masjid={ws.masjid} timetable={ws.timetable} power={screen.power} salahBlank={screen.salahBlank !== false} broadcasts={bcs} className="stage-fill" label={`${screen.name} as it appears on the screen`} />
        </button>
      ) : <div className="live-frame empty"><b>Nothing published yet</b><span>Design this screen in the Studio, then press Send Live.</span></div>}
    </section>
  );
}

/* ---------- Screens ---------- */

function ScreensCard({ screens, now, focusId, onPick }: { screens: Screen[]; now: number; focusId?: string; onPick: (id: string) => void }) {
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  // Clicking a screen shows it in the big live preview (its own page is under Screens). When the preview is out of
  // view (a narrow window, scrolled down), bring it into view so the change is seen.
  const pick = (id: string) => {
    onPick(id);
    const live = document.querySelector<HTMLElement>('.card.live');
    if (!live) return;
    const r = live.getBoundingClientRect();
    if (r.top < 0 || r.bottom > window.innerHeight) live.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <section className="card glass screens" aria-labelledby="scr">
      <div className="card-head"><h2 id="scr">Screens</h2><span className="count">{screens.length}</span><span className="spacer" /><button type="button" className="link" onClick={() => nav('/screens')}>See all</button></div>
      {screens.length ? (
        <div className="scr-grid">
          {screens.map((s) => {
            const st = screenStatus(s, now);
            const d = liveDesign(ws, s.id, now);
            return (
              <button key={s.id} type="button" className={`scr ctl${s.id === focusId ? ' sel' : ''}`} onClick={() => pick(s.id)} aria-pressed={s.id === focusId} aria-label={`Show ${s.name} in the live preview`}>
                <span className={`thumb${st === 'offline' || st === 'unpaired' ? ' dim' : ''}`}>
                  {d && <ScreenThumb screen={s} design={d} />}
                </span>
                <span className="meta"><b>{s.name}</b><span className={`pill ${STATUS_PILL[st]}`}>{STATUS_LABEL[st]}</span><small>{seenAgo(s, now)}</small></span>
              </button>
            );
          })}
        </div>
      ) : <div className="empty"><span>No screens yet.</span></div>}
    </section>
  );
}

/** A screen's thumbnail: what's live on it, announcements and appeals included. */
function ScreenThumb({ screen: s, design }: { screen: Screen; design: Design }) {
  const ws = useApp((x) => x.ws)!;
  const bcs = useScreenBroadcasts(s.id);
  return <StageView design={design} masjid={ws.masjid} timetable={ws.timetable} power={s.power} salahBlank={s.salahBlank !== false} broadcasts={bcs} className="stage-fill" label={`${s.name} thumbnail`} />;
}

/* ---------- Notifications ---------- */

const KIND_ICON: Record<ActivityItem['kind'], { icon: IconName; tone: string }> = {
  'screen-offline': { icon: 'monitor', tone: 'danger' }, 'screen-online': { icon: 'monitor', tone: 'success' }, published: { icon: 'send', tone: 'accent' },
  timetable: { icon: 'upload', tone: 'accent' }, schedule: { icon: 'calendar', tone: 'accent' }, eid: { icon: 'moon', tone: 'accent' }, paired: { icon: 'tv', tone: 'success' }, settings: { icon: 'gear', tone: 'accent' }, salah: { icon: 'moon', tone: 'accent' },
  message: { icon: 'megaphone', tone: 'danger' }, appeal: { icon: 'heart', tone: 'accent' }, khutbah: { icon: 'khutbah', tone: 'accent' }, team: { icon: 'users', tone: 'accent' },
};

/** Notices and warnings sent by Noor MS (activity kinds 'noorms-notice' and 'noorms-warning'). */
const isNoorMS = (kind: string) => kind.startsWith('noorms-');

export function ago(iso: string, now = Date.now()) {
  const m = Math.round((now - Date.parse(iso)) / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  if (h < 48) return 'Yesterday';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function ActivityList({ items, limit }: { items: ActivityItem[]; limit?: number }) {
  const list = limit ? items.slice(0, limit) : items;
  if (!list.length) return <div className="empty"><span>No activity yet.</span></div>;
  return (
    <ul className="notes">
      {list.map((a) => {
        const noorms = isNoorMS(a.kind);
        const warning = a.kind === ('noorms-warning' as string);
        const k = noorms ? { icon: 'megaphone' as IconName, tone: warning ? 'danger' : 'accent' } : KIND_ICON[a.kind] ?? { icon: 'bell' as IconName, tone: 'accent' };
        return (
          <li key={a.id} className={`note ctl${noorms ? ' noorms' : ''}`}>
            <span className={`note-ic tone-${k.tone}`}><Icon name={k.icon} size={22} /></span>
            <span className="note-tx">
              {noorms && <em className={warning ? 'warn' : ''}>{warning ? 'Warning from Noor MS' : 'Notice from Noor MS'}</em>}
              <b>{a.title}</b><small>{a.detail}</small>
            </span>
            <span className="note-at"><small>{ago(a.at)}</small>{!a.read && <i className="dot" style={{ background: 'var(--accent)', width: 9, height: 9 }} aria-label="Unread" />}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Notifications({ items, onClose }: { items: ActivityItem[]; onClose: () => void }) {
  const [all, setAll] = useState(false);
  return (
    <div className="notes-panel">
      <div className="card-head"><h2 style={{ fontSize: 26, fontWeight: 700 }}>Notifications</h2><span className="spacer" />
        <button type="button" className="link" style={{ color: 'var(--label)' }} onClick={() => attempt(() => backend.markActivityRead())}>Mark all read</button></div>
      <div style={{ maxHeight: all ? 520 : undefined, overflow: 'auto' }}><ActivityList items={items} limit={all ? 100 : 5} /></div>
      {items.length > 5 && <button type="button" className="btn plain block lg" onClick={() => setAll((v) => !v)}>{all ? 'Show less' : 'See all activity'}</button>}
      <span className="sr"><button type="button" onClick={onClose}>Close</button></span>
    </div>
  );
}
